let fallbackCounter = 0

const UUID_BYTE_LENGTH = 16

interface AnalyticsCrypto {
  randomUUID?: () => string
  getRandomValues?: (array: Uint8Array) => Uint8Array
}

export function generateId(): string {
  const runtimeCrypto = getRuntimeCrypto()

  if (typeof runtimeCrypto?.randomUUID === 'function') {
    try {
      return runtimeCrypto.randomUUID()
    } catch {
      // Continue to the analytics-only fallback chain below.
    }
  }

  const webCryptoId = generateIdWithGetRandomValues(runtimeCrypto)
  if (webCryptoId) {
    return webCryptoId
  }

  return generateAnalyticsFallbackId()
}

function getRuntimeCrypto(): AnalyticsCrypto | undefined {
  return typeof globalThis.crypto === 'object' && globalThis.crypto !== null
    ? (globalThis.crypto as unknown as AnalyticsCrypto)
    : undefined
}

function generateIdWithGetRandomValues(runtimeCrypto: AnalyticsCrypto | undefined): string | null {
  if (typeof runtimeCrypto?.getRandomValues !== 'function') {
    return null
  }

  try {
    const bytes = new Uint8Array(UUID_BYTE_LENGTH)
    runtimeCrypto.getRandomValues(bytes)
    return formatUuidV4(bytes)
  } catch {
    return null
  }
}

function generateAnalyticsFallbackId(): string {
  const bytes = new Uint8Array(UUID_BYTE_LENGTH)
  const now = Date.now()
  fallbackCounter = (fallbackCounter + 1) % 0x100000000
  const randomA = Math.floor(Math.random() * 0x100000000)
  const randomB = Math.floor(Math.random() * 0x100000000)

  bytes[0] = Math.floor(now / 0x10000000000) & 0xff
  bytes[1] = Math.floor(now / 0x100000000) & 0xff
  bytes[2] = Math.floor(now / 0x1000000) & 0xff
  bytes[3] = Math.floor(now / 0x10000) & 0xff
  bytes[4] = Math.floor(now / 0x100) & 0xff
  bytes[5] = now & 0xff

  bytes[6] = Math.floor(fallbackCounter / 0x1000000) & 0xff
  bytes[7] = Math.floor(fallbackCounter / 0x10000) & 0xff
  bytes[8] = Math.floor(fallbackCounter / 0x100) & 0xff
  bytes[9] = fallbackCounter & 0xff

  bytes[10] = Math.floor(randomA / 0x1000000) & 0xff
  bytes[11] = Math.floor(randomA / 0x10000) & 0xff
  bytes[12] = Math.floor(randomA / 0x100) & 0xff
  bytes[13] = randomA & 0xff
  bytes[14] = Math.floor(randomB / 0x100) & 0xff
  bytes[15] = randomB & 0xff

  return formatUuidV4(bytes)
}

function formatUuidV4(bytes: Uint8Array): string {
  const uuidBytes = new Uint8Array(bytes)
  uuidBytes[6] = (uuidBytes[6] & 0x0f) | 0x40
  uuidBytes[8] = (uuidBytes[8] & 0x3f) | 0x80

  const hex = Array.from(uuidBytes, (byte) => byte.toString(16).padStart(2, '0'))
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
}

export function isoTimestamp(): string {
  return new Date().toISOString()
}

export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

export function estimateSize(obj: unknown): number {
  return new Blob([JSON.stringify(obj)]).size
}
